/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react';
import { Search, Clock, Instagram, Facebook, Youtube, Linkedin, Eye, Calendar, Twitter, X, Trash2 } from 'lucide-react';
import DraftPostCard from '../components/DraftPostCard';
import SchedulePostPanel from '../components/SchedulePostPanel';
import ContentsService from '../../../content-creation/services/contents.services';
import { MarketingListParams, PostReqParams } from '../../../../data/types/contentMarketing.interface';
import ContentMarketingService from '../../services/contentMarketing.services';
import moment from 'moment';
import VideoPlayer from '../../../shared/components/VideoPlayer';
import LoadingBackdrop from '../../../shared/components/LoadingBackdrop';
import { lowerCase } from 'lodash';
import ConfirmationDialog from '../../../shared/components/ConfirmationDialog';

export default function MediaLibrary() {
  const contentCache = useRef(new Map());
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFormat, setSelectedFormat] = useState<'all' | 'carousel' | 'static' | 'short' | 'long' | 'story'>(
    'all'
  );
  const [activeTab, setActiveTab] = useState<'posts' | 'scheduled'>('posts');
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [contents, setContents] = useState<any[]>([]);
  const [selectedPost, setSelectedPost] = useState<any | null>(null);
  const [scheduledPosts, setScheduledPosts] = useState<any[]>([]);
  const [selectedScheduledPost, setSelectedScheduledPost] = useState<any | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage] = useState(50);
  const [totalPages, setTotalPages] = useState(1);
  const [listFilterParams, setListFilterParams] = useState<MarketingListParams>({ perPage: 50, page: 1, type: 'final' });
  const [loading, setLoading] = useState(false);
  const [showAlert, setShowAlert] = useState(false);

  const fetchRealContent = async (url: string) => {
    try {
      if (!url) return '';
      if (contentCache.current.has(url)) {
        return contentCache.current.get(url);
      }
      let contentUrl = '';

      if (url.includes('localhost')) {
        const accessContent = await ContentsService.fetchRealS3Content(url);
        contentUrl = accessContent.url;
      } else {
        contentUrl = await ContentsService.fetchRealDropboxContent(url);
      }
      contentCache.current.set(url, contentUrl);
      return contentUrl;
    } catch (error: any) {
      if (error.response?.status === 409) {
        console.warn("Conflict:", error.response.data);
        return (error.response.data?.data || []);
      } else {
        console.error("Unexpected error", error);
      }
    }
  };

  const getAllFinalContents = async () => {
    try {
      if (loading) return;
      setLoading(true);
      const result = await ContentMarketingService.getFinalFiles({ perPage, page, type: 'final', }, 'video');
      setTotalPages(result.totalPages || 1);
      const contents = await Promise.all(
        result.list.map(async (item: any) => {
          const url = await fetchRealContent(item.videoUrl);
          return { ...item, url, contentId: item.id, contentType: item.assetContentType.name, };
        })
      );
      setContents(contents);
    } catch (error) {
      console.error('Error fetching active posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const getScheduledPosts = async () => {
    try {
      const result = await ContentMarketingService.getContents({ ...listFilterParams, scheduledPost: activeTab === 'scheduled', perPage, page });
      setTotalPages(result.totalPages || 1);
      const contents = await Promise.all(
        result.list.map(async (item: any) => {
          const platformDetails = item.marketingPlatformDetails?.[0] || {};
          const url = await fetchRealContent(item.videoUrl);
          const { title: _ignored, ...cleanPlatformDetails } = platformDetails;

          return {
            contentId: item.id,
            title: item.title,
            videoUrl: url,
            ...item.assetContentType,
            ...cleanPlatformDetails,
          };
        })
      );
      setScheduledPosts(contents);
    } catch (error) {
      console.error('Error fetching scheduled posts:', error);
    }
  };

  const fetchData = async () => {
    if (activeTab === 'scheduled') {
      await getScheduledPosts();
    } else {
      await getAllFinalContents();
    }
  };

  useEffect(() => {
    fetchData();
  }, [activeTab, page]);

  useEffect(() => {
    setPage(1);
    setListFilterParams({
      ...listFilterParams, scheduledPost: activeTab === 'scheduled'
    })
  }, [activeTab]);

  const getPlatformIcon = (platform: string) => {
    switch (lowerCase(platform)) {
      case 'instagram':
        return <Instagram size={14} className="text-[#6B7280]" />;
      case 'facebook':
        return <Facebook size={14} className="text-[#6B7280]" />;
      case 'youtube':
        return <Youtube size={14} className="text-[#6B7280]" />;
      case 'linkedin':
        return <Linkedin size={14} className="text-[#6B7280]" />;
      case 'twitter':
        return <Twitter size={14} className="text-[#6B7280]" />;
      default:
        return null;
    }
  };

  const handleConfirm = async () => {
    try {
      setLoading(true);
      await ContentMarketingService.delete(selectedPost.contentType, selectedPost.contentId, selectedPost.platform);
      if (activeTab === 'scheduled') {
        getScheduledPosts();
      } else {
        getAllFinalContents();
      }
    } catch (error) {
      console.error('Error deleting scheduled post:', error);
    } finally {
      setLoading(false);
    }
  }

  const handleDeletePost = async (post: any) => {
    setShowAlert(true);
    setSelectedPost(post)
  };

  const handleOpenPanel = (draft: any) => {
    setSelectedPost(draft);
    setIsPanelOpen(true);
  };

  const handleClosePanel = () => {
    setSelectedPost(null);
    setIsPanelOpen(false);
  };

  const handleSchedulePost = async (id: number, scheduleData: PostReqParams, action: string) => {
    try {
      setLoading(true);
      if (action === 'schedule') {
        scheduleData.platforms.forEach((platform: string) => {
          ContentMarketingService.publishMarketingDetails(id, scheduleData.contentType, platform, scheduleData);
        });
        alert('Post Scheduled successfully');
        handleClosePanel();
      } else {
        const platform = scheduleData.platforms[0];
        const result = await ContentMarketingService.updatePublishedContentDetails(scheduleData.contentType, id, platform, scheduleData);
        if (result) {
          alert(`Updated Scheduled post successfully for ${platform}`);
          handleClosePanel();
        }
      }
    } catch (err) {
      console.error(err);
      alert('Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const handleSavePost = async (contId: number, updatedData: any, action: string) => {
    try {
      setLoading(true);
      if (action === 'publish') {
        const result = await ContentMarketingService.publishMarketingDetails(contId, updatedData.contentType, updatedData.platforms[0], updatedData);
        if (result) {
          alert('Publishing started');
          handleClosePanel();
        }
      } else {
        const platform = updatedData.platforms[0];
        const result = await ContentMarketingService.updatePublishedContentDetails(updatedData.contentType, contId, platform, updatedData);
        if (result) {
          alert(`Updated published post successfully for ${platform}`);
          handleClosePanel();
        }
      }
    } catch (err) {
      console.error(err);
      alert('Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-[1600px] mx-auto">
      {/* Header with Tabs */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-[#111827] mb-4">Media Library</h1>

        {/* Tab Navigation */}
        <div className="flex items-center gap-1 border-b border-[#E5E7EB]">
          <button
            onClick={() => setActiveTab('posts')}
            className={`px-4 py-2.5 text-sm font-medium transition-all relative ${activeTab === 'posts' ? 'text-[#F67570]' : 'text-[#6B7280] hover:text-[#111827]'
              }`}
          >
            Post Contents
            {activeTab === 'posts' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#F67570]" />}
          </button>
          <button
            onClick={() => setActiveTab('scheduled')}
            className={`px-4 py-2.5 text-sm font-medium transition-all relative ${activeTab === 'scheduled' ? 'text-[#F67570]' : 'text-[#6B7280] hover:text-[#111827]'
              }`}
          >
            Scheduled Posts
            {scheduledPosts.length > 0 && (
              <span className="ml-2 px-2 py-0.5 text-xs font-semibold bg-[#FEF2F2] text-[#F67570] rounded-full">
                {scheduledPosts.length}
              </span>
            )}
            {activeTab === 'scheduled' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#F67570]" />}
          </button>
        </div>
      </div>

      {/* Search and Filter Bar */}
      <div className="bg-white border border-[#E5E7EB] rounded-xl p-4 mb-5 shadow-sm">
        <div className="flex flex-col lg:flex-row gap-3 items-start lg:items-center justify-between">
          {/* Search */}
          <div className="relative w-full lg:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" size={16} />
            <input
              type="text"
              placeholder="Search posts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full h-9 pl-10 pr-4 text-sm bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#F67570] focus:ring-1 focus:ring-[#F67570]"
            />
          </div>

          {/* Format Filters */}
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setSelectedFormat('all')}
              className={`px-3 py-1 text-xs font-medium rounded-full transition-all ${selectedFormat === 'all'
                ? 'bg-[#FEF2F2] text-[#F67570] shadow-sm'
                : 'text-[#6B7280] hover:bg-[#F9FAFB] hover:text-[#111827]'
                }`}
            >
              All
            </button>
            <button
              onClick={() => setSelectedFormat('carousel')}
              className={`px-3 py-1 text-xs font-medium rounded-full transition-all ${selectedFormat === 'carousel'
                ? 'bg-[#FEF2F2] text-[#F67570] shadow-sm'
                : 'text-[#6B7280] hover:bg-[#F9FAFB] hover:text-[#111827]'
                }`}
            >
              Carousel
            </button>
            <button
              onClick={() => setSelectedFormat('static')}
              className={`px-3 py-1 text-xs font-medium rounded-full transition-all ${selectedFormat === 'static'
                ? 'bg-[#FEF2F2] text-[#F67570] shadow-sm'
                : 'text-[#6B7280] hover:bg-[#F9FAFB] hover:text-[#111827]'
                }`}
            >
              Static
            </button>
            <button
              onClick={() => setSelectedFormat('short')}
              className={`px-3 py-1 text-xs font-medium rounded-full transition-all ${selectedFormat === 'short'
                ? 'bg-[#FEF2F2] text-[#F67570] shadow-sm'
                : 'text-[#6B7280] hover:bg-[#F9FAFB] hover:text-[#111827]'
                }`}
            >
              Shorts
            </button>
            <button
              onClick={() => setSelectedFormat('long')}
              className={`px-3 py-1 text-xs font-medium rounded-full transition-all ${selectedFormat === 'long'
                ? 'bg-[#FEF2F2] text-[#F67570] shadow-sm'
                : 'text-[#6B7280] hover:bg-[#F9FAFB] hover:text-[#111827]'
                }`}
            >
              Long Video
            </button>
            <button
              onClick={() => setSelectedFormat('story')}
              className={`px-3 py-1 text-xs font-medium rounded-full transition-all ${selectedFormat === 'story'
                ? 'bg-[#FEF2F2] text-[#F67570] shadow-sm'
                : 'text-[#6B7280] hover:bg-[#F9FAFB] hover:text-[#111827]'
                }`}
            >
              Story
            </button>
          </div>
        </div>
      </div>

      {/* Results Count */}
      <div className="mb-3">
        <p className="text-xs text-[#6B7280]">
          {activeTab === 'posts'
            ? `${contents.length} ${contents.length === 1 ? 'post' : 'posts'}`
            : `${scheduledPosts.length} ${scheduledPosts.length === 1 ? 'post' : 'posts'}`}
        </p>
      </div>

      {/* Contents ready to publish - View mode */}
      {activeTab === 'posts' && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {contents.map((draft) => (
              <DraftPostCard
                key={draft.contentId}
                post={draft}
                onDelete={() => handleDeletePost(draft)}
                onOpenPanel={handleOpenPanel}
              />
            ))}
          </div>

          {/* Empty State for Drafts */}
          {contents.length === 0 && (
            <div className="bg-white border border-[#E5E7EB] rounded-xl p-12 text-center">
              <div className="w-16 h-16 bg-[#F9FAFB] border border-[#E5E7EB] rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock size={28} className="text-[#6B7280]" />
              </div>
              <h3 className="font-medium text-[#111827] mb-2">No posts found</h3>
              <p className="text-sm text-[#6B7280]">Try adjusting your search or filter criteria</p>
            </div>
          )}
        </>
      )}

      {/* Scheduled Posts View */}
      {activeTab === 'scheduled' && (
        <>
          {scheduledPosts.length > 0 ? (
            <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden">
              <table className="w-full">
                <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider">
                      Post Title
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider">
                      Platform
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider">
                      Scheduled Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider">
                      Time
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E5E7EB]">
                  {scheduledPosts.map((post) => (
                    <tr key={post.contentId} className="hover:bg-[#F9FAFB] transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {post.videoUrl && (
                            <img
                              src={post.videoUrl}
                              alt=""
                              className="w-10 h-10 object-cover rounded border border-[#E5E7EB]"
                            />
                          )}
                          <span className="text-sm font-medium text-[#111827] line-clamp-1">{post.title}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {getPlatformIcon(post.platform)}
                          <span className="text-sm text-[#6B7280]">{post.platform}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-[#6B7280]">
                          {moment(post?.scheduledDate).format('YYYY-MM-DD')}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-[#6B7280]">{moment(post?.scheduledDate).format('LT')}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full bg-[#FEF2F2] text-[#F67570] border border-[#F67570]/20">
                          <Clock size={12} className="mr-1" />
                          Scheduled
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {/* View Button */}
                          <button
                            onClick={() => {
                              setSelectedScheduledPost(post);
                              setIsViewModalOpen(true);
                            }}
                            className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-[#F67570] hover:bg-[#FEF2F2] rounded-lg transition-colors"
                          >
                            <Eye size={14} className="mr-1.5" />
                            View Content
                          </button>

                          {/* Delete Button */}
                          <button
                            onClick={() => handleDeletePost(post)}
                            className="inline-flex items-center justify-center p-2 text-[#F67570] hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                {showAlert && selectedPost && (
                  <ConfirmationDialog
                    action="deleteSchedulePost"
                    confirm={handleConfirm}
                    setShowAlert={setShowAlert}
                    value={selectedPost?.title || ''}
                  />
                )}
              </table>
            </div>
          ) : (
            <div className="bg-white border border-[#E5E7EB] rounded-xl p-12 text-center">
              <div className="w-16 h-16 bg-[#F9FAFB] border border-[#E5E7EB] rounded-full flex items-center justify-center mx-auto mb-4">
                <Calendar size={28} className="text-[#6B7280]" />
              </div>
              <h3 className="font-medium text-[#111827] mb-2">No scheduled posts</h3>
              <p className="text-sm text-[#6B7280] mb-4">Schedule draft posts to see them here</p>
              <button
                onClick={() => setActiveTab('posts')}
                className="px-4 py-2 text-sm font-medium text-white bg-[#F67570] hover:bg-[#F45C56] rounded-lg transition-colors"
              >
                View Post Contents
              </button>
            </div>
          )}
        </>
      )}

      {/* Schedule Post Panel */}
      {isPanelOpen && selectedPost && (
        <SchedulePostPanel
          post={selectedPost}
          isLoading={loading}
          onClose={handleClosePanel}
          onSchedulePost={handleSchedulePost}
          onSavePost={handleSavePost}
        />
      )}

      {/* View Scheduled Post Content Modal */}
      {isViewModalOpen && selectedScheduledPost && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-[#E5E7EB] px-6 py-4 flex items-center justify-between">
              <h2 className="font-semibold text-[#111827]">Post Content</h2>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  setIsViewModalOpen(false);
                  setSelectedScheduledPost(null);
                }}
                className="p-2 hover:bg-[#F9FAFB] rounded-lg transition-colors"
              >
                <X size={20} className="text-[#6B7280]" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-6">
              {/* Media Preview */}
              {selectedScheduledPost.videoUrl && (
                <div className="aspect-square rounded-lg overflow-hidden border border-[#E5E7EB]">
                  <VideoPlayer videoUrl={selectedScheduledPost.videoUrl} thumbnail={selectedScheduledPost?.thumbnail} />
                </div>
              )}

              {/* Post Title */}
              {selectedScheduledPost.title && (
                <div>
                  <label className="block text-xs font-medium text-[#6B7280] mb-2">Post Title</label>
                  <p className="text-sm text-[#111827]">{selectedScheduledPost.title}</p>
                </div>
              )}

              {/* Caption */}
              {selectedScheduledPost.caption && (
                <div>
                  <label className="block text-xs font-medium text-[#6B7280] mb-2">Caption</label>
                  <div className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg p-4">
                    <p className="text-sm text-[#111827] whitespace-pre-wrap">{selectedScheduledPost.caption}</p>
                  </div>
                </div>
              )}

              {/* Hashtags */}
              {selectedScheduledPost.hashtags && (
                <div>
                  <label className="block text-xs font-medium text-[#6B7280] mb-2">Hashtags</label>
                  <p className="text-sm text-[#F67570]">{selectedScheduledPost.hashtags}</p>
                </div>
              )}

              {/* Schedule Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-[#6B7280] mb-2">Scheduled Date</label>
                  <p className="text-sm text-[#111827]">
                    {moment(selectedScheduledPost.scheduledDate).format('YYYY-MM-DD')}
                  </p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#6B7280] mb-2">Time</label>
                  <p className="text-sm text-[#111827]">{moment(selectedScheduledPost.scheduledDate).format('LT')}</p>
                </div>
              </div>

              {/* Platforms */}
              {selectedScheduledPost.platforms && selectedScheduledPost.platforms.length > 0 && (
                <div>
                  <label className="block text-xs font-medium text-[#6B7280] mb-2">Platforms</label>
                  <div className="flex items-center gap-2 flex-wrap">
                    {selectedScheduledPost.platforms.map((platform: string, idx: number) => (
                      <span
                        key={idx}
                        className="inline-flex items-center px-3 py-1.5 text-xs font-medium bg-[#F9FAFB] text-[#6B7280] border border-[#E5E7EB] rounded-lg"
                      >
                        {getPlatformIcon(platform)}
                        <span className="ml-1.5 capitalize">{platform}</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-white border-t border-[#E5E7EB] px-6 py-4">
              <button
                onClick={() => {
                  setIsViewModalOpen(false);
                  setSelectedScheduledPost(null);
                }}
                className="w-full px-4 py-2 text-sm font-medium text-white bg-[#F67570] hover:bg-[#F45C56] rounded-lg transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* pagination */}
      {totalPages > 1 && (
        <div className="flex flex-col items-center justify-center mt-10 gap-4">

          {/* Page Info */}
          <div className="text-sm text-gray-500">
            Page {page} of {totalPages}
          </div>

          {/* Controls */}
          <div className="flex items-center gap-2">

            {/* Previous */}
            <button
              disabled={page === 1}
              onClick={() => setPage((prev) => prev - 1)}
              className={`px-4 py-2 rounded-lg border text-sm font-medium transition-all duration-200 ${page === 1
                ? 'cursor-not-allowed text-gray-400 border-gray-200'
                : 'text-[#F67570] border-[#F67570] hover:bg-[#FEF2F2]'
                }`}
            >
              Previous
            </button>

            {/* Page Numbers */}
            {[...Array(totalPages)].map((_, index) => {
              const page = index + 1;
              if (page > totalPages) {
                return (
                  <button
                    key={page}
                    onClick={() => setPage(page)}
                    className={`px-4 py-2 rounded-lg border text-sm font-medium transition-all duration-200 ${page === totalPages
                      ? 'bg-[#F67570] text-white border-[#F67570]'
                      : 'text-gray-600 border-gray-200 hover:bg-gray-50'
                      }`}
                  >
                    {page}
                  </button>
                );
              }
            })}

            {/* Next */}
            <button
              disabled={page === totalPages}
              onClick={() => setPage((prev) => prev + 1)}
              className={`px-4 py-2 rounded-lg border text-sm font-medium transition-all duration-200 ${page === totalPages
                ? 'cursor-not-allowed text-gray-400 border-gray-200'
                : 'text-[#F67570] border-[#F67570] hover:bg-[#FEF2F2]'
                }`}
            >
              Next
            </button>

          </div>
        </div>
      )}

      <LoadingBackdrop openBackdrop={loading} />
    </div>
  );
}
